// «Рынок → Розница → Tonusmeal»: розничные цены сетей по строкам корзины за день.
// Источник внутренний (нет разрешения на показ клиенту) — ручка за признаком
// `prices_internal`, вкладку источника показывает только такому кабинету.

import { useQuery } from '@tanstack/react-query'
import { cabinetApi } from '../api'
import { DASH, formatDay, formatNumber, useLang, useT } from '../i18n'
import { chainCities, chainDays, cityKey, storeKey } from './chains'
import { productKeys } from './customs'
import { usePricesUrl } from './pricesUrl'

export function ChainsPanel() {
  const t = useT()
  const { lang } = useLang()
  const meta = useQuery({ queryKey: ['cabinet', 'prices', 'chains'],
    queryFn: () => cabinetApi.retailChainDays(), retry: false })
  const all = meta.data?.days ?? []
  const cities = chainCities(all)
  const url = usePricesUrl()
  const pickCity = url.get('chainsCity')
  const city = (pickCity && cities.includes(pickCity) ? pickCity : cities[0]) ?? ''
  const days = chainDays(all, city)
  const pickDay = url.get('chainsDay')
  const day = pickDay && days.includes(pickDay) ? pickDay : days[0] ?? ''
  const series = useQuery({ queryKey: ['cabinet', 'prices', 'chains', city, day],
    queryFn: () => cabinetApi.retailChains(city, day), retry: false, enabled: !!day })

  if (meta.isLoading) return <p className="text-sm text-mist">{t('prices.loading')}</p>
  if (meta.error) return <p className="text-sm text-mist">{t('prices.error')}</p>
  if (!all.length) return <p className="text-sm text-foam">{t('prices.chains.empty')}</p>

  const data = series.data
  const stores = data?.stores ?? []
  const num = (v: number | undefined) => (v === undefined ? DASH : formatNumber(lang, v, 0))
  const cityLabel = (c: string) => { const k = cityKey(c); return k ? t(k) : c }
  const storeLabel = (s: string) => { const k = storeKey(s); return k ? t(k) : s }
  const select = 'rounded border border-line bg-transparent px-2 py-1 text-foam'

  return (
    <div className="space-y-4 text-sm">
      <div className="flex flex-wrap items-center gap-4 text-mist">
        <label className="flex items-center gap-2">
          {t('prices.chains.city')}
          <select value={city} onChange={(e) => url.set({ chainsCity: e.target.value, chainsDay: null })}
            className={select}>
            {cities.map((c) => <option key={c} value={c}>{cityLabel(c)}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2">
          {t('prices.chains.day')}
          <select value={day} onChange={(e) => url.set({ chainsDay: e.target.value })} className={select}>
            {days.map((d) => <option key={d} value={d}>{formatDay(lang, d)}</option>)}
          </select>
        </label>
      </div>
      {series.isLoading && <p className="text-mist">{t('prices.loading')}</p>}
      {data && (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-xs">
            <thead>
              <tr className="text-left text-mist">
                <th className="py-1 pr-3 font-normal">{t('prices.export.product')}</th>
                {stores.map((s) => (
                  <th key={s} className="py-1 pr-3 text-right font-normal">{storeLabel(s)}</th>
                ))}
                <th className="py-1 pr-3 text-right font-normal text-foam">{t('prices.chains.median')}</th>
                <th className="py-1 pr-3 text-right font-normal">{t('prices.chains.range')}</th>
                <th className="py-1 pr-3 text-right font-normal">{t('prices.chains.points')}</th>
              </tr>
            </thead>
            {data.lines.map((ln) => (
              <tbody key={ln.code}>
                <tr className="border-t border-line text-mist">
                  <td colSpan={stores.length + 4} className="pb-1 pt-3 font-medium text-foam">
                    {ln.code}. {ln.name}
                    {!ln.rows.length && <span className="ml-2 font-normal text-mist">{t('prices.chains.none')}</span>}
                  </td>
                </tr>
                {ln.rows.map((r) => (
                  <tr key={r.item_key} className="border-t border-line/30 text-foam">
                    <td className="py-1 pl-3 pr-3">
                      {lang === 'ru' ? r.name_ru : r.name_en}
                      <span className="text-mist">{' · '}{productKeys(r).map((k) => t(k)).join(', ')}</span>
                    </td>
                    {stores.map((s) => (
                      <td key={s} className="py-1 pr-3 text-right tabular-nums">{num(r.stores[s])}</td>
                    ))}
                    <td className="py-1 pr-3 text-right font-medium tabular-nums">{num(r.median)}</td>
                    <td className="py-1 pr-3 text-right tabular-nums text-mist">
                      {num(r.min)}–{num(r.max)}
                    </td>
                    <td className="py-1 pr-3 text-right tabular-nums text-mist">{r.points}</td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
          <p className="mt-2 text-xs text-mist">{t('prices.chains.unit')}</p>
          <p className="mt-1 text-xs text-mist">{t('prices.chains.note')}</p>
        </div>
      )}
    </div>
  )
}
